import {useMemo} from 'react'
import dayjs from 'dayjs'
import {Virtuoso} from 'react-virtuoso'
import {createColumnHelper, flexRender, getCoreRowModel, useReactTable} from '@tanstack/react-table'
import {Checkbox} from '@/components/commons/checkbox/ChekBox'
import {Badge} from '@/components/ui/badge/Badge'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {AlertCircleIcon, Download01Icon, Send01Icon} from '@/components/ui/icon/Icon'
import {
    TableBodyCell,
    TableBodyCellText,
    TableBodyRow,
    TableHeader,
    TableHeaderCell,
    TableHeaderCellLabel
} from '@/components/ui/table-atoms/TableAtoms'
import {useTranslation} from '@/translations/i18n'
import {SiarExportPreviewItem} from '@/features/projects/siar-bulk-export/types'
import {
    siarPreviewProgressStatusToBadgeVariant,
    siarPreviewProgressStatusToLabel
} from '@/features/projects/siar-bulk-export/utils'
import {
    StyledBulkSiarExportInOtherBatchIcon,
    StyledBulkSiarExportPreviewTableInner,
    StyledBulkSiarExportPreviewTableWrapper,
    StyledBulkSiarExportPreviewVirtuosoBody
} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/style'

type BulkSiarExportPreviewTableProps = {
    items: SiarExportPreviewItem[]
    isSelected: (projectId: string) => boolean
    onToggle: (projectId: string) => void
    onToggleAll: () => void
    masterChecked: boolean
    masterIndeterminate: boolean
    onLoadMore: () => void
}

const formatDate = (value: string | null): string => (value ? dayjs(value).format('DD/MM/YYYY') : '—')
const formatPriCode = (code: number): string => `PRI-${String(code).padStart(4, '0')}`

const columnHelper = createColumnHelper<SiarExportPreviewItem>()

const VIRTUOSO_HEIGHT = 380

export const BulkSiarExportPreviewTable = ({
    items,
    isSelected,
    onToggle,
    onToggleAll,
    masterChecked,
    masterIndeterminate,
    onLoadMore
}: BulkSiarExportPreviewTableProps) => {
    const {t} = useTranslation()

    const columns = useMemo(
        () => [
            columnHelper.display({
                id: 'select',
                size: 40,
                header: () => (
                    <Checkbox
                        id="bulk-siar-export-master-checkbox"
                        checked={masterChecked}
                        indeterminate={masterIndeterminate}
                        onChange={onToggleAll}
                    />
                ),
                cell: ({row}) => (
                    <span onClick={e => e.stopPropagation()}>
                        <Checkbox
                            id={`bulk-siar-export-row-${row.original.projectId}`}
                            checked={isSelected(row.original.projectId)}
                            onChange={() => onToggle(row.original.projectId)}
                        />
                    </span>
                )
            }),
            columnHelper.accessor('projectCode', {
                id: 'projectCode',
                size: 110,
                header: () => t('projects:bulk_siar_export_column_pri_code'),
                cell: ({row}) => (
                    <Flexbox align="center" gap={1}>
                        <TableBodyCellText>{formatPriCode(row.original.projectCode)}</TableBodyCellText>
                        {row.original.inOtherBatch && (
                            <StyledBulkSiarExportInOtherBatchIcon
                                title={t('projects:bulk_siar_export_in_other_batch_tooltip')}
                            >
                                <AlertCircleIcon size={14} />
                            </StyledBulkSiarExportInOtherBatchIcon>
                        )}
                    </Flexbox>
                )
            }),
            columnHelper.accessor('patientFullName', {
                id: 'patientFullName',
                size: 180,
                header: () => t('projects:bulk_siar_export_column_patient'),
                cell: ({getValue}) => <TableBodyCellText>{getValue()}</TableBodyCellText>
            }),
            columnHelper.accessor('startDate', {
                id: 'startDate',
                size: 110,
                header: () => t('projects:start_date'),
                cell: ({getValue}) => <TableBodyCellText>{formatDate(getValue())}</TableBodyCellText>
            }),
            columnHelper.accessor('expirationDate', {
                id: 'expirationDate',
                size: 110,
                header: () => t('projects:bulk_siar_export_column_expiration'),
                cell: ({getValue}) => <TableBodyCellText>{formatDate(getValue())}</TableBodyCellText>
            }),
            columnHelper.accessor('endDate', {
                id: 'endDate',
                size: 130,
                header: () => t('projects:bulk_siar_export_column_end_date'),
                cell: ({getValue}) => <TableBodyCellText>{formatDate(getValue())}</TableBodyCellText>
            }),
            columnHelper.accessor('progressStatus', {
                id: 'progressStatus',
                size: 160,
                header: () => t('projects:bulk_siar_export_column_status'),
                cell: ({getValue}) => {
                    const status = getValue()
                    return (
                        <Badge variant={siarPreviewProgressStatusToBadgeVariant[status]} size="sm">
                            {status === 'to_transmit' ? <Send01Icon size={12} /> : <Download01Icon size={12} />}
                            {t(siarPreviewProgressStatusToLabel[status])}
                        </Badge>
                    )
                }
            })
        ],
        [t, masterChecked, masterIndeterminate, onToggleAll, isSelected, onToggle]
    )

    const table = useReactTable({
        data: items,
        columns,
        getCoreRowModel: getCoreRowModel()
    })

    const rows = table.getRowModel().rows
    const headerGroups = table.getHeaderGroups()

    return (
        <StyledBulkSiarExportPreviewTableWrapper>
            <StyledBulkSiarExportPreviewTableInner>
                <TableHeader>
                    {headerGroups.map(headerGroup =>
                        headerGroup.headers.map(header => (
                            <TableHeaderCell key={header.id} cellSize={header.getSize()} isPinned={false}>
                                <TableHeaderCellLabel>
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(header.column.columnDef.header, header.getContext())}
                                </TableHeaderCellLabel>
                            </TableHeaderCell>
                        ))
                    )}
                </TableHeader>
                <StyledBulkSiarExportPreviewVirtuosoBody>
                    <Virtuoso
                        style={{height: VIRTUOSO_HEIGHT}}
                        data={rows}
                        totalCount={rows.length}
                        overscan={200}
                        endReached={onLoadMore}
                        itemContent={(_index, row) => (
                            <TableBodyRow
                                isSelected={isSelected(row.original.projectId)}
                                onClick={() => onToggle(row.original.projectId)}
                                style={{cursor: 'pointer'}}
                            >
                                <Flexbox fullWidth>
                                    {row.getVisibleCells().map(cell => (
                                        <TableBodyCell key={cell.id} cellSize={cell.column.getSize()}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableBodyCell>
                                    ))}
                                </Flexbox>
                            </TableBodyRow>
                        )}
                    />
                </StyledBulkSiarExportPreviewVirtuosoBody>
            </StyledBulkSiarExportPreviewTableInner>
        </StyledBulkSiarExportPreviewTableWrapper>
    )
}
